/* „Wybierz plan" / „Zmień plan" — the cards under „Twój plan", from
 * `docs/design/subskrypcja-2026-09-21/index.html`, variant A in Flame Dark (the owner's choice of
 * 21 September 2026). Frame `?v=0&t=dark`; `shots/a-dark.png` is the render it was read back
 * against. Flame is a supported theme and every value below is a token, so it inverts with the rest.
 *
 * ── Three cards, one difference ─────────────────────────────────────────────────────────────
 * The canon's own reasoning, and it decides the layout: the seats line is the ONLY thing that
 * differs between the plans, so it is the only line with a ground of its own, and the features
 * every plan carries are one row under the cards rather than three repeated lists.
 *
 * ── Where the ember is allowed to appear ────────────────────────────────────────────────────
 * On the recommended level's button and nowhere else on this block (owner-approved polish,
 * 4 November 2026). The recommended card's ember ring, the current plan's flag and the launch
 * line's ember ink were withdrawn with it: the accent marks the one act, not three facts.
 *
 * ── The buttons sit on one baseline ─────────────────────────────────────────────────────────
 * `.cta` takes `margin-top: auto` inside a column card with a floor, which is what puts three
 * different card bodies' buttons on the same line. The floor is a height, so it is layout and
 * stays a literal here, as `AccountScreen.module.css` states for its own.
 *
 * Every colour, space, radius and type value is a token; heights and widths are layout. */

.cards {
  display: grid;
  grid-auto-columns: minmax(0, 1fr);
  grid-auto-flow: column;
  gap: var(--space-3);
}

/* ── The block is the container the cards are measured against ──────────────────────────────
 * Not the window. The sheet's width is the window MINUS the rail and minus the side column's
 * `clamp(380px, 33vw, 540px)`, so a window-keyed breakpoint says nothing about how much room these
 * cards actually have: at 1280x620 the old `width < 1080px` rule kept three cards up in 698px of
 * block and drew them 226px wide with 186px buttons, well under the canon's card. The block asks
 * itself instead, and the answer is the same at every window that produces that width. */
.block {
  container-type: inline-size;
}

/* The section's header: its name, and the period switch flush right. One control tall in every
 * state, so the switch arriving with the catalogue changes nothing under it. */
.head {
  display: flex;
  gap: var(--space-3);
  align-items: center;
  justify-content: space-between;
  min-height: var(--h-field);
  margin-bottom: var(--space-3);

  /* The saving in the switch, in neutral ink: the accent on this screen is the primary's alone. */
  --seg-badge-ink: var(--text-2);
}

.title {
  font-size: var(--fs-note);
  font-weight: var(--fw-medium);
  color: var(--text-2);
}

/* The card's own floor keeps the three equal while their bodies differ — the Pro card carries a
 * period switch and a promotional line that the other two do not. It is the LOADED Pro card's
 * height (`--h-account-plan-card`), so the skeleton's box and the answer's box are one box. */
.card {
  display: flex;
  flex-direction: column;
  min-height: var(--h-account-plan-card);
  padding: var(--space-5);
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-20px);
  box-shadow: var(--shadow-card);
}

/* The current plan, marked once: a neutral outline and its refused „Twój plan" button. */
.current {
  border-color: var(--text-3);
  box-shadow: none;
}

.name {
  display: flex;
  gap: var(--space-2);
  align-items: center;
  justify-content: space-between;
  font-size: var(--fs-day-title);
  font-weight: var(--fw-extrabold);
  letter-spacing: var(--ls-title);
  color: var(--text);
}

.flag {
  padding: var(--space-3px) var(--space-7px);
  font-size: var(--fs-pl-micro);
  font-weight: var(--fw-extrabold);
  letter-spacing: var(--ls-eyebrow);
  color: var(--text-2);
  text-transform: uppercase;
  white-space: nowrap;
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--r-pill);
}

/* The tagline keeps its two lines' worth of room whether it has one line or two, so three cards
 * whose sentences wrap differently still put their price rows on one line. */
.sub {
  min-height: 34px;
  margin-top: var(--space-1);
  font-size: var(--fs-note);
  line-height: var(--lh-hint-dense);
  color: var(--text-2);
}

.price {
  display: flex;
  gap: var(--space-7px);
  align-items: baseline;
  margin-top: var(--space-14px);
}

.amount {
  font-size: var(--fs-h1);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-none);
  letter-spacing: var(--ls-home-figure);
  color: var(--text);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

/* The unit on the amount's baseline, and the billing note on its own line under it. The note's line
 * is reserved on every card, so cards whose faces say different things still put their seats on one
 * line. */
.unit {
  font-size: var(--fs-caption);
  color: var(--text-2);
}

.priceNote {
  min-height: 1lh;
  margin-top: var(--space-2);
  font-size: var(--fs-caption);
  color: var(--text-2);
}

/* The launch offer, on the yearly face only. No fill: it is a fact about the price above it rather
 * than a second call to action.
 *
 * ONE colour for the whole line, and since 4 November 2026 a neutral one: the owner withdrew the
 * muted ember it wore from 22 September, because the accent belongs to the primary alone. */
.promo {
  margin-top: var(--space-2);
  font-size: var(--fs-caption);
  font-weight: var(--fw-bold);
  color: var(--text-2);
}

/* ── The trial code (R19) ────────────────────────────────────────────────────────────────────
 * Two rows on the Pro card alone. The CONTROL row is one control tall (`--h-account-code-row`, the
 * field's frame) in every face, so „Masz kod?", the field and „Kod PGB · Usuń" replace one another
 * in place. The FACT row is the card's promo line: the launch offer, the trial's sentence, the
 * month's note and a refused code's reason all stand in it, and it keeps the two lines the
 * longest of them wraps to at the three-up width. Both are `min-height`, never `height`: text
 * zoomed past what was measured grows the card instead of running into the seats line.
 *
 * The card floors grow with the control row only where it is drawn (`.withCode`): a subscriber's
 * cards carry no code row and keep the floor they had. */
.trial {
  display: flex;
  flex-direction: column;
  margin-top: var(--space-3);

  /* „Masz kod?", „Usuń" and „Przełącz na rocznie" in body ink: the accent is the primary's. */
  --inline-text-ink: var(--text);
}

.trialControl {
  display: flex;
  align-items: center;
  min-height: var(--h-account-code-row);
}

.trialFact {
  min-height: 2lh;
  margin-top: var(--space-2);
  font-size: var(--fs-caption);
}

/* „Masz kod?" and „Kod PGB · Usuń": the card's caption size, so neither outgrows its one line, and
 * a word in a line rather than a bar across the card — the field is the only thing here that
 * stretches. */
.trialText {
  font-size: var(--fs-caption);
  color: var(--text-2);
}

.trialCode {
  font-weight: var(--fw-bold);
  color: var(--text);
}

/* A code standing on the month is not a verdict, it is a note — secondary ink, no ember. */
.trialNote {
  margin-top: var(--space-2);
  font-size: var(--fs-caption);
  color: var(--text-2);
}

/* The fact row owns the gap above it, so the line inside it adds none. */
.trialFact .promo,
.trialFact .trialNote {
  margin-top: var(--space-0);
}

/* A refused code's reason, in the fact row. The strong ink, as under every other field: the
 * lighter one is under AA for a caption line. */
.trialError {
  font-weight: var(--fw-semibold);
  color: var(--danger-text-strong);
}

/* „Zastosuj", inside the field's frame: the ghost's treatment, this surface's geometry. */
.trialApply {
  height: var(--h-detail-tiny-btn);
  padding: var(--space-0) var(--space-3);
  font-size: var(--fs-caption);
  font-weight: var(--fw-bold);
  background: transparent;
  border-radius: var(--radius-sm);
}

/* The one line that differs between the plans. */
.seats {
  margin-top: var(--space-14px);
  font-size: var(--fs-note);
  line-height: var(--lh-hint);
  color: var(--text);
}

/* `auto` is what puts every card's button on one baseline — see the header. */
.cta {
  padding-top: var(--space-14px);
  margin-top: auto;
}

/* The card's button: the screen's pill at the card's full width. Only the width is this file's —
 * the treatment is emberButton's or the screen's secondary, and the geometry the screen's `.pill`. */
.button {
  width: 100%;
}

/* The features every plan carries, once. */
.every {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-6px) var(--space-14px);
  padding-top: var(--space-14px);
  margin-top: var(--space-4);
  font-size: var(--fs-label);
  color: var(--text-2);
  border-top: var(--border-hairline) solid var(--border);
}

.everyTitle {
  font-weight: var(--fw-bold);
  color: var(--text);
}

/* The dot is decoration — the words are the list, and the dot says nothing a listener needs. */
.everyItem::before {
  display: inline-block;
  width: 5px;
  height: 5px;
  margin-right: var(--space-7px);
  vertical-align: middle;
  content: '';
  background: var(--text-3);
  border-radius: var(--r-circle);
}

/* The fine print. `--text-3` measured 2.55:1 on the sheet in Flame, which is not readable text at
 * any size — and this is the line that says what is being charged and when. `--text-2` is the
 * app's secondary ink and clears AA at both themes; the lead keeps its step above it. */
.fine {
  margin-top: var(--space-14px);
  font-size: var(--fs-micro);
  line-height: var(--lh-relaxed);
  color: var(--text-2);
}

.fineLead {
  font-weight: var(--fw-semibold);
  color: var(--text);
}

/* Nothing to buy — the provider is unreachable and every web row came back without a price. It
 * stands in the CARDS' box rather than instead of it: the reserve is the same one the skeleton and
 * the three cards stand in, so the three states of this block are one box. */
.nothing {
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: var(--h-account-plan-card);
  padding: var(--space-5);
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-20px);
  box-shadow: var(--shadow-card);
}

/* The floor with the Pro card's code row in it, on the routes that draw the row — see `.trial`. */
.withCode .card,
.withCode .nothing {
  min-height: var(--h-account-plan-card-code);
}

.nothingRetry {
  margin-top: var(--space-3);
}

/* The refusal, under the cards and above the shared row: it is the answer to the button that was
 * just pressed, so it belongs beside the buttons rather than at the foot of the block. */
.refusal {
  margin-top: var(--space-3);
}

.retry {
  margin-top: var(--space-3);
}

/* ── The wait ────────────────────────────────────────────────────────────────────────────────
 * The skeleton stands INSIDE the card's own reserve: the same grid, the same three boxes, the same
 * floor, so the answer landing changes ink and not geometry. The bars are the shape of the content
 * — a name, a tagline, a price, the seats box and a button — rather than one grey slab. */
.skeletonName {
  width: 96px;
  height: var(--fs-day-title);
}

.skeletonSub {
  width: 100%;
  height: var(--fs-note);
  margin-top: var(--space-3);
}

.skeletonAmount {
  width: 128px;
  height: var(--fs-h1);
  margin-top: var(--space-14px);
}

.skeletonSeats {
  width: 100%;
  height: var(--fs-note);
  margin-top: var(--space-14px);
}

.skeletonButton {
  width: 100%;
  height: var(--h-account-button);
  margin-top: auto;
}

/* Three cards need three times the canon's card plus two gaps; below that the block has one card's
 * width and the cards stack. One column, because a 226px plan card is a card nobody reads — the
 * same answer `AccountScreen.module.css` gives for the side column, asked of the right box.
 *
 * ── 720, not 760 ────────────────────────────────────────────────────────────────────────────
 * The threshold has to have room on BOTH sides of it. At 1512x862 the block measures 760.016px,
 * which is 0.016px from a rule written at 760: a hair of zoom, a scrollbar appearing, a font
 * metric, and the canon's own viewport flips to one column. 720 clears every window that draws
 * three (1440 and 1512) and is still above every window that stacks (1280).
 *
 * ── The reserve holds in the fallback too ───────────────────────────────────────────────────
 * This block used to set `min-height: 0` here, which deleted the reserve at exactly the viewport
 * that needs it most: at 1280x620 the block is 698px, the fallback is live by default, and the
 * three pending boxes stood ~40px tall against loaded cards of ~300px — the features row, the fine
 * print and everything under them stepped down about 800px the moment `/me/plans` answered. A
 * stacked card is a different shape from a three-up one (the tagline and the price share the width
 * rather than wrapping), so it gets its OWN measured floor rather than reusing the wide card's. */
@container (width < 720px) {
  .cards {
    grid-template-columns: minmax(0, 1fr);
  }

  .card,
  .nothing {
    min-height: var(--h-account-plan-card-stacked);
  }

  .withCode .card,
  .withCode .nothing {
    min-height: var(--h-account-plan-card-code-stacked);
  }

  /* One column has the width for every face's sentence on one line. */
  .trialFact {
    min-height: 1lh;
  }
}
